import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "@/lib/firebase/config";
import { COLLECTIONS } from "@/lib/firebase/firestore";
import type {
  RoomType,
  RoomTypeFormData,
  Room,
  RoomFormData,
  RoomStatus,
} from "@/types/rooms";

// ─── Room Types ───────────────────────────────────────────────────────────────

export async function getActiveRoomTypes(): Promise<RoomType[]> {
  const q = query(
    collection(db, COLLECTIONS.ROOM_TYPES),
    where("active", "==", true),
    orderBy("sortOrder", "asc"),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as RoomType);
}

export async function getRoomTypeBySlug(
  slug: string,
): Promise<RoomType | null> {
  const q = query(
    collection(db, COLLECTIONS.ROOM_TYPES),
    where("slug", "==", slug),
    where("active", "==", true),
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() } as RoomType;
}

export async function getRoomTypes(): Promise<RoomType[]> {
  const q = query(
    collection(db, COLLECTIONS.ROOM_TYPES),
    orderBy("sortOrder", "asc"),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as RoomType);
}

export async function getRoomType(id: string): Promise<RoomType | null> {
  const snap = await getDoc(doc(db, COLLECTIONS.ROOM_TYPES, id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as RoomType;
}

export async function createRoomType(data: RoomTypeFormData): Promise<string> {
  const ref = await addDoc(collection(db, COLLECTIONS.ROOM_TYPES), {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateRoomType(
  id: string,
  data: Partial<RoomTypeFormData>,
): Promise<void> {
  await updateDoc(doc(db, COLLECTIONS.ROOM_TYPES, id), {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteRoomType(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTIONS.ROOM_TYPES, id));
}

export async function updateRoomTypeImages(
  id: string,
  images: string[],
): Promise<void> {
  await updateDoc(doc(db, COLLECTIONS.ROOM_TYPES, id), {
    images,
    updatedAt: serverTimestamp(),
  });
}

// ─── Rooms ────────────────────────────────────────────────────────────────────

export async function getRooms(roomTypeId?: string): Promise<Room[]> {
  const constraints = roomTypeId
    ? [where("roomTypeId", "==", roomTypeId), orderBy("number", "asc")]
    : [orderBy("number", "asc")];

  const q = query(collection(db, COLLECTIONS.ROOMS), ...constraints);
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Room);
}

export async function getRoom(id: string): Promise<Room | null> {
  const snap = await getDoc(doc(db, COLLECTIONS.ROOMS, id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as Room;
}

export async function createRoom(data: RoomFormData): Promise<string> {
  const ref = await addDoc(collection(db, COLLECTIONS.ROOMS), {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateRoom(
  id: string,
  data: Partial<RoomFormData>,
): Promise<void> {
  await updateDoc(doc(db, COLLECTIONS.ROOMS, id), {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

export async function updateRoomStatus(
  id: string,
  status: RoomStatus,
  notes?: string,
): Promise<void> {
  await updateDoc(doc(db, COLLECTIONS.ROOMS, id), {
    status,
    ...(notes !== undefined && { notes }),
    updatedAt: serverTimestamp(),
  });
}

export async function deleteRoom(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTIONS.ROOMS, id));
}